/**
 * Long forms (2026-10-15, tester fixes A6): people filled in a section reached from a link (e.g.
 * "Change your travel distance" on the EMT profile) and left without saving, because the only Save
 * button was at the very bottom of a long page. Every long form now gets:
 *   - useDirtyTracker: "is anything unsaved?" from a snapshot of the form's values. The baseline is
 *     taken the first time the person touches the form (so the page filling itself in on load never
 *     counts as a change), and again at the first touch after each successful save (markClean).
 *   - StickySaveBar: a bar pinned to the bottom of the screen, phone and desktop, while there are
 *     unsaved changes: "You have unsaved changes" + the form's own Save button.
 *   - useUnsavedChangesGuard: a warning before leaving with unsaved changes. Covers closing or
 *     reloading the tab (the browser's own prompt), in-app links (our dialog: Save and leave / Leave
 *     without saving / Stay) and the browser Back button (same dialog). The app uses <BrowserRouter>,
 *     where React Router's useBlocker is not available, hence the listeners.
 *   - useSectionHighlight: a link into a section (#job-preferences) scrolls to it and outlines it for
 *     a moment, so it is obvious where to look.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

/** Unsaved-change tracking from a snapshot (any JSON-able value of the form's fields). */
export function useDirtyTracker(snapshot: unknown) {
  const current = JSON.stringify(snapshot ?? null);
  const baseline = useRef<string | null>(null);
  const [, force] = useState(0);
  const arm = useCallback(() => {
    if (baseline.current === null) baseline.current = current;
  }, [current]);
  // After a save the baseline is taken again at the next touch, like on load: the page reloads what
  // it saved and some values come back in another form (a number for typed text), which kept the bar up.
  const markClean = useCallback(() => {
    baseline.current = null;
    force((n) => n + 1);
  }, []);
  /** Spread on the form's container: the first touch (pointer, key or focus) takes the baseline. */
  const trackProps = {
    onPointerDownCapture: arm,
    onKeyDownCapture: arm,
    onFocusCapture: arm,
  };
  const dirty = baseline.current !== null && baseline.current !== current;
  return { dirty, markClean, trackProps };
}

/**
 * The warning before leaving with unsaved changes. `onSave` returns true when the save worked.
 * Returns the dialog element to render.
 */
export function useUnsavedChangesGuard(
  dirty: boolean,
  onSave?: () => Promise<boolean>,
  opts: { back?: boolean } = {},
): { dialog: React.ReactElement; leaveTo: (href: string) => void; leaveBack: (steps?: number) => void } {
  // Pages that move on by themselves after saving (Post a Job) turn the Back-button guard off, so the
  // extra history entry never outlives the page.
  const guardBack = opts.back !== false;
  const navigate = useNavigate();
  const [pending, setPending] = useState<{ kind: 'link'; href: string } | { kind: 'back' } | { kind: 'history'; steps: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const sentinel = useRef(false);
  const leaving = useRef(false);

  // Closing or reloading the tab: the browser's own "Leave site?" prompt.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (leaving.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // In-app links: stop the click before React Router sees it, ask first.
  useEffect(() => {
    if (!dirty) return;
    const onClick = (e: MouseEvent) => {
      if (leaving.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // A link to another part of the same page (#section) is not leaving.
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setPending({ kind: 'link', href: url.pathname + url.search + url.hash });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty]);

  // Back button: while dirty, one extra history entry for this same page; Back lands on it (nothing
  // changes on screen) and asks. Removed again once the form is saved.
  useEffect(() => {
    if (!guardBack || leaving.current) return;
    if (dirty && !sentinel.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      sentinel.current = true;
    } else if (!dirty && sentinel.current) {
      sentinel.current = false;
      window.history.back();
    }
  }, [dirty, guardBack]);
  useEffect(() => {
    if (!guardBack) return;
    const onPop = () => {
      if (leaving.current || !dirtyRef.current || !sentinel.current) return;
      sentinel.current = false;
      setPending({ kind: 'back' });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [guardBack]);

  const leave = (p: NonNullable<typeof pending>) => {
    leaving.current = true;
    setPending(null);
    if (p.kind === 'back') {
      window.history.back();
      return;
    }
    if (p.kind === 'history') {
      // The page's own Back: one real step back (the page, tab and scroll the user came from), skipping
      // the extra entry the browser-Back guard may have added.
      const extra = sentinel.current ? 1 : 0;
      sentinel.current = false;
      window.history.go(-(p.steps + extra));
      return;
    }
    if (sentinel.current) {
      // Drop the extra entry first, so Back from the next page does not land here twice.
      sentinel.current = false;
      const once = () => {
        window.removeEventListener('popstate', once);
        navigate(p.href);
      };
      window.addEventListener('popstate', once);
      window.history.back();
    } else navigate(p.href);
  };
  const stay = () => {
    if (pending?.kind === 'back' && guardBack) {
      window.history.pushState(window.history.state, '', window.location.href);
      sentinel.current = true;
    }
    setPending(null);
  };
  const saveAndLeave = async () => {
    if (!pending || !onSave) return;
    const p = pending;
    leaving.current = true;
    setSaving(true);
    try {
      if (await onSave()) leave(p);
      else leaving.current = false;
    } catch {
      leaving.current = false;
    } finally {
      setSaving(false);
    }
  };

  /** For the page's own Back / Cancel buttons (they navigate in code, not through a link). */
  const leaveTo = (href: string) => {
    if (dirtyRef.current) setPending({ kind: 'link', href });
    else navigate(href);
  };
  /** The page's own Back when there is a page to go back to (useBackTarget().fromHistory). */
  const leaveBack = (steps = 1) => {
    if (dirtyRef.current) setPending({ kind: 'history', steps });
    else leave({ kind: 'history', steps });
  };

  const dialog = (
    <Dialog open={pending !== null} onOpenChange={(o) => (!o ? stay() : undefined)}>
      <DialogContent data-testid="unsaved-changes-dialog">
        <DialogHeader>
          <DialogTitle>You have unsaved changes</DialogTitle>
          <DialogDescription>If you leave now, the changes on this page are lost.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="min-h-[2.75rem]" onClick={stay} data-testid="unsaved-stay">
            Stay on this page
          </Button>
          <Button variant="outline" className="min-h-[2.75rem]" onClick={() => pending && leave(pending)} data-testid="unsaved-leave">
            Leave without saving
          </Button>
          {onSave && (
            <Button className="min-h-[2.75rem]" disabled={saving} onClick={() => void saveAndLeave()} data-testid="unsaved-save-and-leave">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              Save and leave
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { dialog, leaveTo, leaveBack };
}


/** The bar pinned to the bottom of the screen while there are unsaved changes. */
export function StickySaveBar({
  dirty,
  saving,
  onSave,
  label = 'Save changes',
  testId = 'sticky-save-bar',
  note = 'You have unsaved changes',
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  label?: string;
  testId?: string;
  note?: string;
}) {
  const shown = dirty || saving;
  // While the bar shows, floating buttons (the EMT helper) move up so they never cover Save.
  useEffect(() => {
    if (!shown || typeof document === 'undefined') return;
    document.body.dataset.stickySave = '1';
    return () => {
      delete document.body.dataset.stickySave;
    };
  }, [shown]);
  if (!shown) return null;
  // Drawn on <body>: inside a page wrapper with a transform, "fixed" is relative to that wrapper and
  // the bar ended up below the screen on the long EMT profile (found by verify-1015-a.mjs a6).
  const bar = (
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.12)]"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        role="region"
        aria-label="Unsaved changes"
        data-testid={testId}
      >
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <p className="text-sm font-medium text-[var(--text-primary)]" aria-live="polite">
            {saving ? 'Saving...' : note}
          </p>
          <Button className="min-h-[2.75rem] shrink-0 px-6" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
            {label}
          </Button>
        </div>
      </div>
  );
  return (
    <>
      {/* Room at the end of the page so the bar never covers the last field. */}
      <div aria-hidden className="h-24" />
      {typeof document === 'undefined' ? bar : createPortal(bar, document.body)}
    </>
  );
}

/** Outline a section for a moment (after a link scrolled to it). */
export function highlightSection(el: Element | null) {
  if (!el) return;
  el.classList.remove('rr-section-highlight');
  // restart the animation
  void (el as HTMLElement).offsetWidth;
  el.classList.add('rr-section-highlight');
  window.setTimeout(() => el.classList.remove('rr-section-highlight'), 2600);
}
